import { useState, useEffect, useRef } from 'react'

function MembersTable({ isDarkMode, teamData, onRemoveMember }) {
	const [openMemberMenu, setOpenMemberMenu] = useState(null)
	const memberMenuRef = useRef(null)

	useEffect(() => {
		const handleClickOutside = event => {
			if (memberMenuRef.current && !memberMenuRef.current.contains(event.target)) {
				setOpenMemberMenu(null)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const members = teamData.members || []

	return (
		<div>
			<h3
				className={`text-lg font-semibold mb-2 ${
					isDarkMode ? 'text-white' : 'text-gray-900'
				}`}
			>
				Members
			</h3>
			{/* Five columns cannot be read on a phone, so below sm: each member is
			    a card carrying the same fields, and the table is hidden. */}
			<div className="space-y-2 sm:hidden">
				{members.length > 0 ? (
					members.map(member => (
						<div
							key={member._id}
							className={`rounded-lg border p-3 ${
								isDarkMode
									? 'border-gray-700 bg-gray-800/50'
									: 'border-gray-200 bg-white'
							}`}
						>
							<p
								className={`break-all text-sm font-semibold ${
									isDarkMode ? 'text-white' : 'text-gray-900'
								}`}
							>
								{member.email}
							</p>
							<div className="mt-2 flex flex-wrap items-center gap-2">
								<span
									className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
										member.role === 'owner'
											? isDarkMode
												? 'bg-blue-500/20 text-blue-400'
												: 'bg-blue-100 text-blue-700'
											: isDarkMode
											? 'bg-gray-700 text-gray-300'
											: 'bg-gray-100 text-gray-700'
									}`}
								>
									{member.role}
								</span>
								<span
									className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
										member.status === 'active'
											? isDarkMode
												? 'bg-green-500/20 text-green-400'
												: 'bg-green-100 text-green-700'
											: isDarkMode
											? 'bg-yellow-500/20 text-yellow-400'
											: 'bg-yellow-100 text-yellow-700'
									}`}
								>
									{member.status}
								</span>
							</div>
							{teamData.team.sharedDevicePool && member.deviceUsage && (
								<p
									className={`mt-2 text-xs ${
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									}`}
								>
									Ext {member.deviceUsage.extensionDevices} · Web{' '}
									{member.deviceUsage.websiteDevices} · Mobile{' '}
									{member.deviceUsage.mobileDevices ?? 0}
								</p>
							)}
							{teamData.isOwner && member.role !== 'owner' && (
								<button
									type="button"
									onClick={() => onRemoveMember(member._id)}
									className={`mt-3 flex min-h-[44px] w-full items-center justify-center rounded-md border text-sm font-semibold ${
										isDarkMode
											? 'border-red-400/30 text-red-300 hover:bg-red-500/20'
											: 'border-red-200 text-red-600 hover:bg-red-50'
									}`}
								>
									Remove
								</button>
							)}
						</div>
					))
				) : (
					<p
						className={`rounded-lg border p-6 text-center text-sm ${
							isDarkMode
								? 'border-gray-700 text-gray-400'
								: 'border-gray-200 text-gray-500'
						}`}
					>
						No members to display
					</p>
				)}
			</div>

			{/* Scrolls sideways on a phone rather than widening the page.
			    contain:paint is what keeps the table's width off the page —
			    overflow alone does not, because a positioned descendant escapes
			    the wrapper. Both are dropped while a member menu is open, so the
			    menu is not clipped. */}
			<div
				className={`hidden sm:block ${
					openMemberMenu
						? 'overflow-visible'
						: 'overflow-x-auto [contain:paint]'
				} rounded-lg border ${
					isDarkMode ? 'border-gray-700' : 'border-gray-200'
				}`}
			>
				<table
					className={`min-w-full divide-y ${
						isDarkMode ? 'divide-gray-700' : 'divide-gray-200'
					}`}
				>
					<thead className={isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}>
						<tr>
							<th
								className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
									isDarkMode ? 'text-gray-400' : 'text-gray-500'
								}`}
							>
								Email
							</th>
							<th
								className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
									isDarkMode ? 'text-gray-400' : 'text-gray-500'
								}`}
							>
								Role
							</th>
							<th
								className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
									isDarkMode ? 'text-gray-400' : 'text-gray-500'
								}`}
							>
								Status
							</th>
							{teamData.team.sharedDevicePool && (
								<th
									className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									}`}
								>
									Devices
								</th>
							)}
							{teamData.isOwner && (
								<th
									className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									}`}
								>
									<span className="sr-only">Actions</span>
								</th>
							)}
						</tr>
					</thead>
					<tbody
						className={`divide-y ${
							isDarkMode
								? 'divide-gray-700 bg-gray-800/50'
								: 'divide-gray-200 bg-white'
						}`}
					>
						{members.length > 0 ? (
							members.map((member, index) => (
								<tr key={member._id}>
									<td
										className={`px-6 py-4 whitespace-nowrap text-sm ${
											isDarkMode ? 'text-gray-300' : 'text-gray-900'
										}`}
									>
										{member.email}
									</td>
									<td
										className={`px-6 py-4 whitespace-nowrap text-sm ${
											isDarkMode ? 'text-gray-400' : 'text-gray-600'
										}`}
									>
										<span
											className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
												member.role === 'owner'
													? isDarkMode
														? 'bg-blue-500/20 text-blue-400'
														: 'bg-blue-100 text-blue-700'
													: isDarkMode
													? 'bg-gray-700 text-gray-300'
													: 'bg-gray-100 text-gray-700'
											}`}
										>
											{member.role}
										</span>
									</td>
									<td
										className={`px-6 py-4 whitespace-nowrap text-sm ${
											isDarkMode ? 'text-gray-400' : 'text-gray-600'
										}`}
									>
										<span
											className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
												member.status === 'active'
													? isDarkMode
														? 'bg-green-500/20 text-green-400'
														: 'bg-green-100 text-green-700'
													: isDarkMode
													? 'bg-yellow-500/20 text-yellow-400'
													: 'bg-yellow-100 text-yellow-700'
											}`}
										>
											{member.status}
										</span>
									</td>
									{teamData.team.sharedDevicePool && (
										<td
											className={`px-6 py-4 whitespace-nowrap text-sm ${
												isDarkMode ? 'text-gray-400' : 'text-gray-600'
											}`}
										>
											{member.deviceUsage ? (
												<div className="flex items-center gap-2">
													<span
														className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
															isDarkMode
																? 'bg-gray-700 text-gray-300'
																: 'bg-gray-100 text-gray-700'
														}`}
														title="Extension devices"
													>
														Ext: {member.deviceUsage.extensionDevices}
													</span>
													<span
														className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
															isDarkMode
																? 'bg-gray-700 text-gray-300'
																: 'bg-gray-100 text-gray-700'
														}`}
														title="Website devices"
													>
														Web: {member.deviceUsage.websiteDevices}
													</span>
													<span
														className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
															isDarkMode
																? 'bg-gray-700 text-gray-300'
																: 'bg-gray-100 text-gray-700'
														}`}
														title="Mobile devices"
													>
														Mobile: {member.deviceUsage.mobileDevices ?? 0}
													</span>
												</div>
											) : (
												<span className="text-xs">-</span>
											)}
										</td>
									)}
									{teamData.isOwner && (
										<td className="px-6 py-4 whitespace-nowrap text-right text-sm">
											{member.role !== 'owner' ? (
												<div className="relative inline-block" ref={openMemberMenu === member._id ? memberMenuRef : null}>
													<button
														onClick={() => setOpenMemberMenu(openMemberMenu === member._id ? null : member._id)}
														className={`p-1 rounded-md hover:bg-opacity-20 ${
															isDarkMode
																? 'text-gray-400 hover:text-gray-300 hover:bg-gray-600'
																: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
														}`}
													>
														<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
															<path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
														</svg>
													</button>
													{openMemberMenu === member._id && (
														<div
															className={`absolute right-0 w-36 rounded-md shadow-lg z-50 border ${
																index === members.length - 1
																	? 'bottom-full mb-1'
																	: 'top-full mt-1'
															} ${
																isDarkMode
																	? 'bg-gray-800 border-gray-700'
																	: 'bg-white border-gray-200'
															}`}
														>
															<button
																onClick={() => {
																	setOpenMemberMenu(null)
																	onRemoveMember(member._id)
																}}
																className={`w-full text-left px-4 py-2 text-sm rounded-md ${
																	isDarkMode
																		? 'text-red-400 hover:bg-gray-700'
																		: 'text-red-600 hover:bg-gray-50'
																}`}
															>
																Remove
															</button>
														</div>
													)}
												</div>
											) : null}
										</td>
									)}
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={teamData.team.sharedDevicePool ? (teamData.isOwner ? 5 : 4) : (teamData.isOwner ? 4 : 3)}
									className={`px-6 py-8 text-center text-sm ${
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									}`}
								>
									No members to display
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default MembersTable
